import { useEffect, useRef, type RefObject } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { measureMatchStatus } from '../measure-match-status'

type UseTrackHighlightMatchStatusArgs = {
  containerRef: RefObject<HTMLElement | null>
  /** The file being shown. */
  contentId: string
  /** The rendered document, or null while it loads. */
  htmlContent: string | null
  passages: string[]
  /** The flag value the document is loaded with; the default until flags finish loading. */
  citationHighlightsImprovements: boolean
  flagLoading: boolean
  /** The file's detected content type, or null when unknown. */
  fileType: string | null
  /** True until the file's record has loaded, so the report waits for its type. */
  fileTypeLoading: boolean
}

/**
 * Report how well a citation's passages were highlighted in the rendered Markdown, once each time
 * the document or the passages change. Counts only, never document text. Works for either
 * highlighter because it reads what was painted.
 */
export function useTrackHighlightMatchStatus(args: UseTrackHighlightMatchStatusArgs): void {
  const { containerRef, contentId, htmlContent, passages, citationHighlightsImprovements, flagLoading, fileType, fileTypeLoading } = args
  const { trackEvent } = useAnalytics()
  const passagesKey = passages.join('\u0000')
  const documentOwner = useRef<{ htmlContent: string | null; contentId: string; citationHighlightsImprovements: boolean } | null>(null)

  useEffect(() => {
    // Right after a switch to another file or flag value, the previous document is still rendered
    // until its replacement starts loading. Each document is attributed to the inputs current when
    // it first renders, and only a document attributed to the current inputs is measured once flags
    // and the file's type have loaded. A download that completes in the same render as the first flag decision is
    // attributed to that decision and can be reported once more after its reload.
    if (documentOwner.current?.htmlContent !== htmlContent) documentOwner.current = { htmlContent, contentId, citationHighlightsImprovements }
    const owner = documentOwner.current
    if (flagLoading || fileTypeLoading || owner.contentId !== contentId || owner.citationHighlightsImprovements !== citationHighlightsImprovements) return
    const container = containerRef.current
    if (!container || !htmlContent || passages.length === 0) return
    trackEvent(AnalyticsEvent.ReferenceHighlightMatchStatus, {
      citationHighlightsImprovements,
      viewer: 'markdown',
      fileType: fileType ?? 'unknown',
      ...measureMatchStatus(container, passages),
    })
    // trackEvent and the passages array are recreated every render; the joined passages key stands
    // in for the passages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationHighlightsImprovements, contentId, fileType, fileTypeLoading, flagLoading, htmlContent, passagesKey])
}
